import type { MessageKey } from "./index.js";

type CompleteUiMessages = Readonly<Record<MessageKey, string>>;

export const fullUiMessageOverrides: Readonly<
  Record<"zh-CN" | "es" | "hi" | "ar" | "pt-BR" | "bn" | "ru", Partial<CompleteUiMessages>>
> = {
  "zh-CN": {
    "common.primaryNavigation": "主导航",
    "common.idle": "空闲",
    "common.connected": "已连接",
    "common.connecting": "正在连接",
    "common.awaitingApproval": "等待批准",
    "common.reconnecting": "正在重新连接",
    "common.disconnected": "未连接",
    "common.error": "错误",
    "common.registered": "已注册",
    "common.pending": "等待中",
    "common.success": "成功",
    "common.running": "运行中",
    "common.failed": "失败",
    "common.ready": "就绪",
    "common.check": "请检查",
    "common.blocked": "已阻止",
    "common.none": "无",
    "common.active": "活动",
    "common.resumeAvailable": "可恢复",
    "common.unchecked": "未检查",
    "common.checking": "检查中",
    "common.reachable": "可访问",
    "common.implemented": "已实现",
    "common.partial": "部分实现",
    "common.planned": "计划中",
    "common.constraint": "限制",
    "common.source": "来源：{source}",
    "common.related": "相关：{reference}",
    "common.codeExample": "{language} 代码示例",
    "notFound.kicker": "404 · 找不到路由",
    "notFound.title": "此运行时中没有该路径的页面。",
    "notFound.path": "未知路由：{path}",
    "notFound.return": "返回文档",
    "landing.proofEndpoint": "一个 MCP 端点",
    "landing.proofOrigin": "绑定 Origin 的配对",
    "landing.proofBrowser": "由浏览器执行",
    "landing.architectureText":
      "标准 MCP 终止于 Bridge。独立的类型化协议将请求传入浏览器，使应用逻辑不进入本地服务和可选的原生外壳。",
    "landing.standardMcp": "01 · 标准 MCP",
    "landing.mcpClient": "MCP 客户端",
    "landing.streamableHttp": "Streamable HTTP",
    "landing.localRouter": "02 · 本地路由器",
    "landing.localBridge": "本地 Bridge",
    "landing.execution": "03 · 执行",
    "landing.browserApp": "BrowserMCP 应用",
    "landing.contextText": "静态应用无需专用服务器或额外客户端配置，也能成为完整的 MCP 运行时。",
    "landing.featureBackendTitle": "无需重复后端",
    "landing.featureBackendText": "处理器就在应用状态、浏览器存储和 Web API 旁运行。",
    "landing.featureBridgeTitle": "一个通用 Bridge",
    "landing.featureBridgeText": "只需配置一次 http://127.0.0.1:8789/mcp，再明确配对应用。",
    "landing.featureTypedTitle": "类型化分离",
    "landing.featureTypedText": "标准 MCP 与内部 Bridge 协议相互分离并分别进行版本管理。",
    "landing.featureStaticTitle": "为静态部署而设计",
    "landing.featureStaticText": "可使用 npm、ESM、Vite 或静态主机，无需应用服务器。",
    "landing.featureManyTitle": "面向多个应用",
    "landing.featureManyText": "Origin、运行时和标签页身份使路由与冲突可被检查。",
    "landing.featureSecurityTitle": "结构化安全",
    "landing.featureSecurityText":
      "仅限 loopback 的传输、精确 Origin 批准、有限配额、取消和日志脱敏。",
    "landing.declareText": "Web 库用精简的公开 API 隐藏传输、协商、关联、超时、取消和重连。",
    "landing.pairingNote":
      "browser 发起连接申请，操作者在本地 Bridge 中批准精确 Origin。browser 端无需 token。",
    "landing.docsText":
      "可见文档与 Docs MCP 共享同一个类型化语料库，支持跨页搜索、API 和类型查询、示例、故障排除、状态、来源和后续参考。",
    "docs.navigation": "文档导航",
    "docs.lede":
      "从一个结构化语料库搜索概念、职责、API 与类型、示例、配置、协议行为、安全限制和诊断。",
    "docs.pages": "{count} 页",
    "docs.sections": "{count} 节",
    "docs.implemented": "{count} 项已实现",
    "docs.planned": "{count} 项计划中",
    "docs.constraints": "{count} 项限制",
    "docs.searchHint": "搜索结构化索引。结果包含状态、来源、准确页面/章节、代码和相关材料。",
    "docs.areasLabel": "文档领域",
    "docs.sectionCount": "{count} 节 →",
    "docs.onThisPageLabel": "本页导航",
    "docs.adjacent": "相邻文档页面",
    "connection.siteRuntime": "BrowserMCP 站点运行时",
    "connection.lede":
      "整个站点是一个 BrowserMCP 应用。配对页面的精确 Origin，建立浏览器会话，并检查全部 Tool、Resource、Prompt、调用、结果和安全错误。",
    "connection.noSession": "没有活动的会话 ID",
    "connection.capabilityKinds": "Tool、Resource 和 Prompt",
    "connection.healthHint": "执行无需凭据的 /health 检查",
    "connection.explicitPairing": "明确批准 Origin",
    "connection.connectText":
      "任何已发布的 HTTPS 页面（包括静态托管）都必须使用 WSS 和受信任的本地证书。本地 HTTP 开发可使用 127.0.0.1 上的 WS。Bridge 只接受 loopback URL。",
    "connection.bridgeUrl": "Bridge WebSocket URL",
    "connection.tokenNote":
      "浏览器提交精确的 Origin 和应用身份。请在 Bridge 管理页面批准待处理请求；静态页面不会接收或保存批准令牌。",
    "connection.approvalPending":
      "保持此标签页打开，在 Bridge 管理页面核对精确 Origin 和应用身份，然后选择 Approve 或 Reject。",
    "connection.readiness": "安全页面准备情况",
    "connection.fourChecks": "批准前的四项检查",
    "connection.recentError": "最近的连接错误",
    "connection.diagnosticOrder": "按照诊断顺序检查",
    "connection.liveSnapshot": "功能实时快照",
    "connection.registeredHere": "已注册到此站点运行时",
    "connection.tools": "Tool",
    "connection.resources": "Resource",
    "connection.prompts": "Prompt",
    "connection.noRegistrations": "没有报告注册项。",
    "connection.requests": "请求",
    "connection.noExecutions": "此标签页中还没有完成浏览器调用。",
    "connection.outcome": "结果",
    "connection.noResult": "尚未记录结果。",
    "connection.noEvents": "尚无诊断事件。",
    "connection.diagnosticTransport": "Loopback 传输",
    "connection.diagnosticTransportReady": "{endpoint} 是仅限 loopback 的端点。",
    "connection.diagnosticCertificate": "受信任的本地证书",
    "connection.diagnosticCertificateSecure":
      "建立 WebSocket 前，浏览器必须信任 Bridge 的 localhost 证书。",
    "connection.diagnosticCertificateDevelopment":
      "本地 HTTP 开发不使用 TLS；公开的 HTTPS 页面必须使用 WSS。",
    "connection.diagnosticCertificateAction":
      "安装并明确信任生成的本地 CA，然后打开 https://{host}/health。不要绕过证书警告。",
    "connection.diagnosticOrigin": "精确 Origin 批准",
    "connection.diagnosticOriginDetail":
      "批准目标必须精确为 {origin}；scheme、host 和 port 都有区别。",
    "connection.diagnosticOriginAction": "在此申请连接，然后在 Bridge 管理页面批准此精确 Origin。",
    "connection.diagnosticNetwork": "本地网络权限",
    "connection.diagnosticNetworkDetail":
      "Chrome 142 限制类似 fetch 的 loopback 请求；Chrome 147 将 LNA 权限扩展到 WebSocket。",
    "connection.diagnosticNetworkAction":
      "出现提示时允许 loopback 访问，并确认 Bridge 的 health 响应允许该 Origin。",
  },
  es: {
    "common.primaryNavigation": "Navegación principal",
    "common.idle": "En espera",
    "common.connected": "Conectado",
    "common.connecting": "Conectando",
    "common.awaitingApproval": "Esperando aprobación",
    "common.reconnecting": "Reconectando",
    "common.disconnected": "Desconectado",
    "common.error": "Error",
    "common.registered": "Registrado",
    "common.pending": "Pendiente",
    "common.success": "Correcto",
    "common.running": "En ejecución",
    "common.failed": "Fallido",
    "common.ready": "Listo",
    "common.check": "Revisar",
    "common.blocked": "Bloqueado",
    "common.none": "Ninguno",
    "common.active": "Activo",
    "common.resumeAvailable": "Reanudación disponible",
    "common.unchecked": "Sin comprobar",
    "common.checking": "Comprobando",
    "common.reachable": "Accesible",
    "common.implemented": "Implementado",
    "common.partial": "Parcial",
    "common.planned": "Planificado",
    "common.constraint": "Restricción",
    "common.source": "Fuente: {source}",
    "common.related": "Relacionado: {reference}",
    "common.codeExample": "Ejemplo de código {language}",
    "notFound.kicker": "404 · Ruta no encontrada",
    "notFound.title": "Este entorno no tiene una página en esa ruta.",
    "notFound.path": "Ruta desconocida: {path}",
    "notFound.return": "Volver a la documentación",
    "landing.proofEndpoint": "Un endpoint MCP",
    "landing.proofOrigin": "Emparejamiento vinculado al Origin",
    "landing.proofBrowser": "Ejecución en el navegador",
    "landing.architectureText":
      "El MCP estándar termina en el Bridge. Un protocolo tipado independiente lleva las solicitudes al navegador y mantiene la lógica de la aplicación fuera del servicio local y de la capa nativa opcional.",
    "landing.standardMcp": "01 · MCP ESTÁNDAR",
    "landing.mcpClient": "Cliente MCP",
    "landing.streamableHttp": "Streamable HTTP",
    "landing.localRouter": "02 · ENRUTADOR LOCAL",
    "landing.localBridge": "Bridge local",
    "landing.execution": "03 · EJECUCIÓN",
    "landing.browserApp": "Aplicación BrowserMCP",
    "landing.contextText":
      "Una aplicación estática puede ser un entorno MCP completo sin servidor propio ni otra entrada de cliente.",
    "landing.featureBackendTitle": "Sin backend duplicado",
    "landing.featureBackendText":
      "Los handlers se ejecutan junto al estado, almacenamiento y APIs web de la aplicación.",
    "landing.featureBridgeTitle": "Un Bridge común",
    "landing.featureBridgeText":
      "Configura http://127.0.0.1:8789/mcp una vez y empareja cada aplicación explícitamente.",
    "landing.featureTypedTitle": "Separación tipada",
    "landing.featureTypedText":
      "El MCP estándar y el protocolo interno del Bridge permanecen separados y versionados.",
    "landing.featureStaticTitle": "Estático por diseño",
    "landing.featureStaticText":
      "Usa npm, ESM, Vite o un host estático. No hace falta un servidor de aplicación.",
    "landing.featureManyTitle": "Preparado para muchas aplicaciones",
    "landing.featureManyText":
      "El Origin y las identidades de runtime y pestaña permiten inspeccionar rutas y colisiones.",
    "landing.featureSecurityTitle": "Seguridad estructural",
    "landing.featureSecurityText":
      "Transporte solo loopback, Origin exacto, límites finitos, cancelación y logs redactados.",
    "landing.declareText":
      "La biblioteca web oculta transporte, negociación, correlación, timeout, cancelación y reconexión tras una API pública pequeña.",
    "landing.pairingNote":
      "El browser solicita acceso y el operador aprueba el Origin exacto en el Bridge local. No se necesita token en el browser.",
    "landing.docsText":
      "La documentación visible y Docs MCP comparten un corpus tipado con búsqueda entre páginas, consulta de API y tipos, ejemplos, diagnóstico, estado, fuentes y referencias siguientes.",
    "docs.navigation": "Navegación de documentación",
    "docs.lede":
      "Busca conceptos, responsabilidades, API y tipos, ejemplos, configuración, protocolo, restricciones de seguridad y diagnóstico en un corpus estructurado.",
    "docs.pages": "{count} páginas",
    "docs.sections": "{count} secciones",
    "docs.implemented": "{count} implementadas",
    "docs.planned": "{count} planificadas",
    "docs.constraints": "{count} restricciones",
    "docs.searchHint":
      "Busca en el índice estructurado. Los resultados incluyen estado, fuente, página y sección exactas, código y material relacionado.",
    "docs.areasLabel": "Áreas de documentación",
    "docs.sectionCount": "{count} secciones →",
    "docs.onThisPageLabel": "Navegación de esta página",
    "docs.adjacent": "Páginas de documentación adyacentes",
    "connection.siteRuntime": "Runtime del sitio BrowserMCP",
    "connection.lede":
      "Todo el sitio es una aplicación BrowserMCP. Empareja el Origin exacto, establece una sesión e inspecciona cada Tool, Resource, Prompt, llamada, resultado y error seguro.",
    "connection.noSession": "No hay ID de sesión activo",
    "connection.capabilityKinds": "Tools, Resources y Prompts",
    "connection.healthHint": "Ejecuta una comprobación /health sin credenciales",
    "connection.explicitPairing": "Aprobación explícita del Origin",
    "connection.connectText":
      "Toda página HTTPS publicada, incluido el alojamiento estático, debe usar WSS con un certificado local de confianza. El desarrollo HTTP local puede usar WS en 127.0.0.1. El Bridge solo acepta URL loopback.",
    "connection.bridgeUrl": "URL WebSocket del Bridge",
    "connection.tokenNote":
      "El navegador envía el Origin exacto y la identidad de la aplicación. Aprueba la solicitud pendiente en la administración del Bridge; la página estática no recibe ni guarda un token.",
    "connection.approvalPending":
      "Mantén abierta esta pestaña, verifica el Origin exacto y la identidad de la aplicación en el Bridge y elige Approve o Reject.",
    "connection.readiness": "Preparación de página segura",
    "connection.fourChecks": "Cuatro comprobaciones antes de aprobar",
    "connection.recentError": "Error de conexión más reciente",
    "connection.diagnosticOrder": "Seguir el orden de diagnóstico",
    "connection.liveSnapshot": "Instantánea activa de capacidades",
    "connection.registeredHere": "Registrado en este runtime del sitio",
    "connection.tools": "Tools",
    "connection.resources": "Resources",
    "connection.prompts": "Prompts",
    "connection.noRegistrations": "No se informó ningún registro.",
    "connection.requests": "Solicitudes",
    "connection.noExecutions": "Aún no ha terminado ninguna llamada del navegador en esta pestaña.",
    "connection.outcome": "Resultado",
    "connection.noResult": "Aún no hay resultados registrados.",
    "connection.noEvents": "Aún no hay eventos de diagnóstico.",
    "connection.diagnosticTransport": "Transporte loopback",
    "connection.diagnosticTransportReady": "{endpoint} es un endpoint limitado a loopback.",
    "connection.diagnosticCertificate": "Certificado local de confianza",
    "connection.diagnosticCertificateSecure":
      "El navegador debe confiar en el certificado localhost del Bridge antes de abrir el WebSocket.",
    "connection.diagnosticCertificateDevelopment":
      "El desarrollo HTTP local no usa TLS; las páginas HTTPS publicadas deben usar WSS.",
    "connection.diagnosticCertificateAction":
      "Instala y confía explícitamente en la CA local generada y abre https://{host}/health. No ignores una advertencia de certificado.",
    "connection.diagnosticOrigin": "Aprobación del Origin exacto",
    "connection.diagnosticOriginDetail":
      "Aprueba exactamente {origin}; scheme, host y port son significativos.",
    "connection.diagnosticOriginAction":
      "Solicita el acceso aquí y aprueba este Origin exacto en la administración del Bridge.",
    "connection.diagnosticNetwork": "Permiso de red local",
    "connection.diagnosticNetworkDetail":
      "Chrome 142 controla las solicitudes loopback tipo fetch; Chrome 147 amplía el permiso LNA a WebSocket.",
    "connection.diagnosticNetworkAction":
      "Permite el acceso loopback cuando se solicite y comprueba que la respuesta health del Bridge permita este Origin.",
  },
  hi: {
    "common.primaryNavigation": "मुख्य नेविगेशन",
    "common.idle": "निष्क्रिय",
    "common.connected": "कनेक्टेड",
    "common.connecting": "कनेक्ट हो रहा है",
    "common.awaitingApproval": "मंज़ूरी की प्रतीक्षा",
    "common.reconnecting": "फिर कनेक्ट हो रहा है",
    "common.disconnected": "डिस्कनेक्टेड",
    "common.error": "त्रुटि",
    "common.registered": "पंजीकृत",
    "common.pending": "लंबित",
    "common.success": "सफल",
    "common.running": "चल रहा है",
    "common.failed": "विफल",
    "common.ready": "तैयार",
    "common.check": "जाँचें",
    "common.blocked": "अवरुद्ध",
    "common.none": "कोई नहीं",
    "common.active": "सक्रिय",
    "common.resumeAvailable": "फिर शुरू किया जा सकता है",
    "common.unchecked": "जाँचा नहीं गया",
    "common.checking": "जाँच जारी है",
    "common.reachable": "पहुँच योग्य",
    "common.implemented": "लागू",
    "common.partial": "आंशिक",
    "common.planned": "नियोजित",
    "common.constraint": "सीमा",
    "common.source": "स्रोत: {source}",
    "common.related": "संबंधित: {reference}",
    "common.codeExample": "{language} कोड उदाहरण",
    "notFound.kicker": "404 · रूट नहीं मिला",
    "notFound.title": "इस रनटाइम में उस पथ का कोई पृष्ठ नहीं है।",
    "notFound.path": "अज्ञात रूट: {path}",
    "notFound.return": "दस्तावेज़ पर लौटें",
    "landing.proofEndpoint": "एक MCP एंडपॉइंट",
    "landing.proofOrigin": "Origin से बँधी पेयरिंग",
    "landing.proofBrowser": "ब्राउज़र में निष्पादन",
    "landing.architectureText":
      "मानक MCP Bridge पर समाप्त होता है। अलग टाइप्ड प्रोटोकॉल अनुरोधों को ब्राउज़र तक ले जाता है और ऐप लॉजिक को स्थानीय सेवा व वैकल्पिक नेटिव शेल से बाहर रखता है।",
    "landing.standardMcp": "01 · मानक MCP",
    "landing.mcpClient": "MCP क्लाइंट",
    "landing.streamableHttp": "Streamable HTTP",
    "landing.localRouter": "02 · स्थानीय राउटर",
    "landing.localBridge": "स्थानीय Bridge",
    "landing.execution": "03 · निष्पादन",
    "landing.browserApp": "BrowserMCP ऐप",
    "landing.contextText":
      "एक स्थिर ऐप अपने सर्वर या अतिरिक्त क्लाइंट एंट्री के बिना सक्षम MCP रनटाइम बन सकता है।",
    "landing.featureBackendTitle": "दोहराया backend नहीं",
    "landing.featureBackendText": "हैंडलर ऐप की स्थिति, ब्राउज़र स्टोरेज और Web API के पास चलते हैं।",
    "landing.featureBridgeTitle": "एक साझा Bridge",
    "landing.featureBridgeText":
      "http://127.0.0.1:8789/mcp एक बार सेट करें, फिर ऐप को स्पष्ट रूप से पेयर करें।",
    "landing.featureTypedTitle": "टाइप्ड विभाजन",
    "landing.featureTypedText": "मानक MCP और आंतरिक Bridge प्रोटोकॉल अलग और संस्करणित रहते हैं।",
    "landing.featureStaticTitle": "स्थिर वितरण के लिए",
    "landing.featureStaticText": "npm, ESM, Vite या स्थिर होस्ट इस्तेमाल करें। ऐप सर्वर आवश्यक नहीं है।",
    "landing.featureManyTitle": "कई ऐप के लिए तैयार",
    "landing.featureManyText": "Origin, रनटाइम और टैब पहचान रूटिंग व टकराव को जाँचने योग्य बनाते हैं।",
    "landing.featureSecurityTitle": "संरचनात्मक सुरक्षा",
    "landing.featureSecurityText":
      "केवल loopback परिवहन, सटीक Origin मंज़ूरी, निश्चित सीमाएँ, रद्द करना और सुरक्षित लॉग।",
    "landing.declareText":
      "वेब लाइब्रेरी छोटी सार्वजनिक API के पीछे परिवहन, नेगोशिएशन, मिलान, टाइमआउट, रद्द करना और पुनः कनेक्शन छिपाती है।",
    "landing.pairingNote":
      "browser access request भेजता है और operator local Bridge में exact Origin approve करता है। browser-side token की जरूरत नहीं है।",
    "landing.docsText":
      "दिखने वाले दस्तावेज़ और Docs MCP एक टाइप्ड कॉर्पस साझा करते हैं जिसमें बहु-पृष्ठ खोज, API और टाइप खोज, उदाहरण, समस्या निवारण, स्थिति, स्रोत और अगले संदर्भ हैं।",
    "docs.navigation": "दस्तावेज़ नेविगेशन",
    "docs.lede":
      "एक संरचित कॉर्पस से अवधारणाएँ, जिम्मेदारियाँ, API व टाइप, उदाहरण, कॉन्फ़िगरेशन, प्रोटोकॉल, सुरक्षा सीमाएँ और निदान खोजें।",
    "docs.pages": "{count} पृष्ठ",
    "docs.sections": "{count} अनुभाग",
    "docs.implemented": "{count} लागू",
    "docs.planned": "{count} नियोजित",
    "docs.constraints": "{count} सीमाएँ",
    "docs.searchHint":
      "संरचित इंडेक्स खोजें। परिणामों में स्थिति, स्रोत, सटीक पृष्ठ/अनुभाग, कोड और संबंधित सामग्री होती है।",
    "docs.areasLabel": "दस्तावेज़ क्षेत्र",
    "docs.sectionCount": "{count} अनुभाग →",
    "docs.onThisPageLabel": "इस पृष्ठ का नेविगेशन",
    "docs.adjacent": "आस-पास के दस्तावेज़ पृष्ठ",
    "connection.siteRuntime": "BrowserMCP साइट रनटाइम",
    "connection.lede":
      "यह पूरी साइट एक BrowserMCP ऐप है। पृष्ठ के सटीक Origin को पेयर करें, ब्राउज़र सत्र बनाएँ और हर Tool, Resource, Prompt, कॉल, परिणाम और सुरक्षित त्रुटि देखें।",
    "connection.noSession": "कोई सक्रिय सत्र ID नहीं",
    "connection.capabilityKinds": "Tool, Resource और Prompt",
    "connection.healthHint": "बिना क्रेडेंशियल की /health जाँच चलाएँ",
    "connection.explicitPairing": "स्पष्ट Origin मंज़ूरी",
    "connection.connectText":
      "स्थिर होस्टिंग सहित हर प्रकाशित HTTPS पृष्ठ को विश्वसनीय स्थानीय प्रमाणपत्र के साथ WSS उपयोग करना चाहिए। स्थानीय HTTP विकास 127.0.0.1 पर WS उपयोग कर सकता है। Bridge केवल loopback URL स्वीकार करता है।",
    "connection.bridgeUrl": "Bridge WebSocket URL",
    "connection.tokenNote":
      "ब्राउज़र सटीक Origin और ऐप पहचान भेजता है। Bridge प्रबंधन पृष्ठ में लंबित अनुरोध मंज़ूर करें; स्थिर पृष्ठ कोई मंज़ूरी टोकन नहीं पाता या सहेजता।",
    "connection.approvalPending":
      "यह टैब खुला रखें, Bridge प्रबंधन में सटीक Origin और ऐप पहचान जाँचें, फिर Approve या Reject चुनें।",
    "connection.readiness": "सुरक्षित पृष्ठ की तैयारी",
    "connection.fourChecks": "मंज़ूरी से पहले चार जाँच",
    "connection.recentError": "सबसे हाल की कनेक्शन त्रुटि",
    "connection.diagnosticOrder": "निदान क्रम का पालन करें",
    "connection.liveSnapshot": "क्षमताओं का लाइव स्नैपशॉट",
    "connection.registeredHere": "इस साइट रनटाइम में पंजीकृत",
    "connection.tools": "Tool",
    "connection.resources": "Resource",
    "connection.prompts": "Prompt",
    "connection.noRegistrations": "कोई पंजीकरण रिपोर्ट नहीं हुआ।",
    "connection.requests": "अनुरोध",
    "connection.noExecutions": "इस टैब में अभी कोई ब्राउज़र कॉल पूरी नहीं हुई है।",
    "connection.outcome": "परिणाम",
    "connection.noResult": "अभी कोई परिणाम दर्ज नहीं है।",
    "connection.noEvents": "अभी कोई निदान घटना नहीं है।",
    "connection.diagnosticTransport": "Loopback ट्रांसपोर्ट",
    "connection.diagnosticTransportReady": "{endpoint} केवल loopback एंडपॉइंट है।",
    "connection.diagnosticCertificate": "विश्वसनीय स्थानीय प्रमाणपत्र",
    "connection.diagnosticCertificateSecure":
      "WebSocket सेटअप से पहले ब्राउज़र को Bridge के localhost प्रमाणपत्र पर भरोसा होना चाहिए।",
    "connection.diagnosticCertificateDevelopment":
      "स्थानीय HTTP विकास TLS नहीं उपयोग करता; प्रकाशित HTTPS पृष्ठों में WSS आवश्यक है।",
    "connection.diagnosticCertificateAction":
      "बनाई गई स्थानीय CA को इंस्टॉल कर स्पष्ट रूप से विश्वसनीय बनाएँ, फिर https://{host}/health खोलें। प्रमाणपत्र चेतावनी को अनदेखा न करें।",
    "connection.diagnosticOrigin": "सटीक Origin मंज़ूरी",
    "connection.diagnosticOriginDetail": "ठीक {origin} को मंज़ूर करें; scheme, host और port महत्वपूर्ण हैं।",
    "connection.diagnosticOriginAction":
      "यहाँ पहुँच का अनुरोध करें, फिर Bridge प्रबंधन में इस सटीक Origin को मंज़ूर करें।",
    "connection.diagnosticNetwork": "स्थानीय नेटवर्क अनुमति",
    "connection.diagnosticNetworkDetail":
      "Chrome 142 fetch-जैसे loopback अनुरोधों को नियंत्रित करता है; Chrome 147 LNA अनुमति को WebSocket तक बढ़ाता है।",
    "connection.diagnosticNetworkAction":
      "पूछे जाने पर loopback पहुँच की अनुमति दें और सुनिश्चित करें कि Bridge का health उत्तर इस Origin को स्वीकार करता है।",
  },
  ar: {
    "common.primaryNavigation": "التنقل الرئيسي",
    "common.idle": "خامل",
    "common.connected": "متصل",
    "common.connecting": "جارٍ الاتصال",
    "common.awaitingApproval": "بانتظار الموافقة",
    "common.reconnecting": "جارٍ إعادة الاتصال",
    "common.disconnected": "غير متصل",
    "common.error": "خطأ",
    "common.registered": "مسجّل",
    "common.pending": "قيد الانتظار",
    "common.success": "نجاح",
    "common.running": "قيد التشغيل",
    "common.failed": "فشل",
    "common.ready": "جاهز",
    "common.check": "تحقق",
    "common.blocked": "محظور",
    "common.none": "لا شيء",
    "common.active": "نشط",
    "common.resumeAvailable": "يمكن الاستئناف",
    "common.unchecked": "غير مفحوص",
    "common.checking": "جارٍ الفحص",
    "common.reachable": "يمكن الوصول إليه",
    "common.implemented": "منفّذ",
    "common.partial": "جزئي",
    "common.planned": "مخطط",
    "common.constraint": "قيد",
    "common.source": "المصدر: {source}",
    "common.related": "مرتبط: {reference}",
    "common.codeExample": "مثال شيفرة {language}",
    "notFound.kicker": "404 · المسار غير موجود",
    "notFound.title": "لا توجد صفحة بهذا المسار في بيئة التشغيل.",
    "notFound.path": "مسار غير معروف: {path}",
    "notFound.return": "العودة إلى التوثيق",
    "landing.proofEndpoint": "نقطة MCP واحدة",
    "landing.proofOrigin": "اقتران مرتبط بـ Origin",
    "landing.proofBrowser": "تنفيذ داخل المتصفح",
    "landing.architectureText":
      "ينتهي MCP القياسي عند Bridge. ينقل بروتوكول مستقل ومحدد الأنواع الطلبات إلى المتصفح، ويُبقي منطق التطبيق خارج الخدمة المحلية والغلاف الأصلي الاختياري.",
    "landing.standardMcp": "01 · MCP القياسي",
    "landing.mcpClient": "عميل MCP",
    "landing.streamableHttp": "Streamable HTTP",
    "landing.localRouter": "02 · الموجّه المحلي",
    "landing.localBridge": "Bridge المحلي",
    "landing.execution": "03 · التنفيذ",
    "landing.browserApp": "تطبيق BrowserMCP",
    "landing.contextText":
      "يمكن للتطبيق الثابت أن يصبح بيئة MCP كاملة بلا خادم خاص أو إعداد عميل إضافي.",
    "landing.featureBackendTitle": "لا حاجة إلى backend مكرر",
    "landing.featureBackendText":
      "تعمل المعالجات بجوار حالة التطبيق وتخزين المتصفح وواجهات Web API.",
    "landing.featureBridgeTitle": "Bridge مشترك واحد",
    "landing.featureBridgeText":
      "اضبط http://127.0.0.1:8789/mcp مرة واحدة ثم اقرن التطبيقات صراحةً.",
    "landing.featureTypedTitle": "فصل محدد الأنواع",
    "landing.featureTypedText":
      "يبقى MCP القياسي وبروتوكول Bridge الداخلي منفصلين ولهما إصدارات مستقلة.",
    "landing.featureStaticTitle": "مصمم للتوزيع الثابت",
    "landing.featureStaticText":
      "استخدم npm أو ESM أو Vite أو استضافة ثابتة، بلا حاجة إلى خادم تطبيق.",
    "landing.featureManyTitle": "مصمم لتطبيقات متعددة",
    "landing.featureManyText":
      "تجعل هويات Origin وبيئة التشغيل وعلامة التبويب التوجيه والتعارضات قابلة للفحص.",
    "landing.featureSecurityTitle": "الأمان جزء من البنية",
    "landing.featureSecurityText":
      "نقل loopback فقط، وموافقة Origin دقيقة، وحدود ثابتة، وإلغاء، وسجلات منقحة.",
    "landing.declareText":
      "تخفي مكتبة الويب النقل والتفاوض والربط والمهلة والإلغاء وإعادة الاتصال خلف API عامة صغيرة.",
    "landing.pairingNote":
      "يرسل browser طلب وصول، ويوافق المشغل على exact Origin في Bridge المحلي. لا يلزم token في browser.",
    "landing.docsText":
      "يشترك التوثيق المرئي وDocs MCP في متن محدد الأنواع يضم البحث بين الصفحات ومرجع API والأنواع والأمثلة والتشخيص والحالة والمصادر والمراجع التالية.",
    "docs.navigation": "التنقل في التوثيق",
    "docs.lede":
      "ابحث في المفاهيم والمسؤوليات وAPI والأنواع والأمثلة والإعداد وسلوك البروتوكول وقيود الأمان والتشخيص ضمن متن منظم واحد.",
    "docs.pages": "{count} صفحة",
    "docs.sections": "{count} قسمًا",
    "docs.implemented": "{count} منفّذ",
    "docs.planned": "{count} مخطط",
    "docs.constraints": "{count} قيود",
    "docs.searchHint":
      "ابحث في الفهرس المنظم. تتضمن النتائج الحالة والمصدر والصفحة والقسم الدقيقين والشيفرة والمواد المرتبطة.",
    "docs.areasLabel": "مجالات التوثيق",
    "docs.sectionCount": "{count} أقسام ←",
    "docs.onThisPageLabel": "التنقل داخل الصفحة",
    "docs.adjacent": "صفحات التوثيق المجاورة",
    "connection.siteRuntime": "بيئة تشغيل موقع BrowserMCP",
    "connection.lede":
      "الموقع كله تطبيق BrowserMCP واحد. اقرن Origin الدقيق للصفحة، وأنشئ جلسة متصفح، وافحص كل Tool وResource وPrompt واستدعاء ونتيجة وخطأ آمن.",
    "connection.noSession": "لا يوجد معرّف جلسة نشط",
    "connection.capabilityKinds": "Tool وResource وPrompt",
    "connection.healthHint": "شغّل فحص /health بلا بيانات اعتماد",
    "connection.explicitPairing": "موافقة صريحة على Origin",
    "connection.connectText":
      "يجب أن تستخدم كل صفحة HTTPS منشورة، بما فيها الاستضافة الثابتة، WSS مع شهادة محلية موثوقة. يمكن للتطوير المحلي عبر HTTP استخدام WS على 127.0.0.1. لا يقبل Bridge إلا عناوين loopback.",
    "connection.bridgeUrl": "عنوان WebSocket لـ Bridge",
    "connection.tokenNote":
      "يرسل المتصفح Origin الدقيق وهوية التطبيق. وافق على الطلب المعلق في صفحة إدارة Bridge؛ ولا تستلم الصفحة الثابتة رمز موافقة أو تحفظه.",
    "connection.approvalPending":
      "أبقِ علامة التبويب مفتوحة، وتحقق من Origin الدقيق وهوية التطبيق في إدارة Bridge، ثم اختر Approve أو Reject.",
    "connection.readiness": "جاهزية الصفحة الآمنة",
    "connection.fourChecks": "أربعة فحوص قبل الموافقة",
    "connection.recentError": "أحدث خطأ اتصال",
    "connection.diagnosticOrder": "اتبع ترتيب التشخيص",
    "connection.liveSnapshot": "لقطة مباشرة للقدرات",
    "connection.registeredHere": "مسجّل في بيئة تشغيل هذا الموقع",
    "connection.tools": "Tool",
    "connection.resources": "Resource",
    "connection.prompts": "Prompt",
    "connection.noRegistrations": "لا توجد تسجيلات مُبلّغ عنها.",
    "connection.requests": "الطلبات",
    "connection.noExecutions": "لم يكتمل أي استدعاء متصفح في علامة التبويب هذه بعد.",
    "connection.outcome": "النتيجة",
    "connection.noResult": "لا توجد نتيجة مسجلة بعد.",
    "connection.noEvents": "لا توجد أحداث تشخيص بعد.",
    "connection.diagnosticTransport": "نقل loopback",
    "connection.diagnosticTransportReady": "{endpoint} نقطة مقصورة على loopback.",
    "connection.diagnosticCertificate": "شهادة محلية موثوقة",
    "connection.diagnosticCertificateSecure":
      "يجب أن يثق المتصفح بشهادة localhost الخاصة بـ Bridge قبل إعداد WebSocket.",
    "connection.diagnosticCertificateDevelopment":
      "لا يستخدم تطوير HTTP المحلي TLS؛ ويجب أن تستخدم صفحات HTTPS المنشورة WSS.",
    "connection.diagnosticCertificateAction":
      "ثبّت سلطة الشهادات المحلية المنشأة وامنحها الثقة صراحةً، ثم افتح https://{host}/health. لا تتجاوز تحذير الشهادة.",
    "connection.diagnosticOrigin": "موافقة Origin دقيقة",
    "connection.diagnosticOriginDetail": "وافق على {origin} تمامًا؛ فكل من scheme وhost وport مهم.",
    "connection.diagnosticOriginAction":
      "اطلب الوصول هنا، ثم وافق على Origin الدقيق هذا في إدارة Bridge.",
    "connection.diagnosticNetwork": "إذن الشبكة المحلية",
    "connection.diagnosticNetworkDetail":
      "يقيّد Chrome 142 طلبات loopback الشبيهة بـ fetch؛ ويوسع Chrome 147 إذن LNA إلى WebSocket.",
    "connection.diagnosticNetworkAction":
      "اسمح بوصول loopback عند الطلب وتأكد من أن استجابة health من Bridge تسمح بهذا Origin.",
  },
  "pt-BR": {
    "common.primaryNavigation": "Navegação principal",
    "common.idle": "Ocioso",
    "common.connected": "Conectado",
    "common.connecting": "Conectando",
    "common.awaitingApproval": "Aguardando aprovação",
    "common.reconnecting": "Reconectando",
    "common.disconnected": "Desconectado",
    "common.error": "Erro",
    "common.registered": "Registrado",
    "common.pending": "Pendente",
    "common.success": "Sucesso",
    "common.running": "Em execução",
    "common.failed": "Falhou",
    "common.ready": "Pronto",
    "common.check": "Verificar",
    "common.blocked": "Bloqueado",
    "common.none": "Nenhum",
    "common.active": "Ativo",
    "common.resumeAvailable": "Retomada disponível",
    "common.unchecked": "Não verificado",
    "common.checking": "Verificando",
    "common.reachable": "Acessível",
    "common.implemented": "Implementado",
    "common.partial": "Parcial",
    "common.planned": "Planejado",
    "common.constraint": "Restrição",
    "common.source": "Fonte: {source}",
    "common.related": "Relacionado: {reference}",
    "common.codeExample": "Exemplo de código {language}",
    "notFound.kicker": "404 · Rota não encontrada",
    "notFound.title": "Este runtime não possui uma página nesse caminho.",
    "notFound.path": "Rota desconhecida: {path}",
    "notFound.return": "Voltar à documentação",
    "landing.proofEndpoint": "Um endpoint MCP",
    "landing.proofOrigin": "Pareamento vinculado ao Origin",
    "landing.proofBrowser": "Execução no navegador",
    "landing.architectureText":
      "O MCP padrão termina no Bridge. Um protocolo tipado separado leva as solicitações ao navegador e mantém a lógica do app fora do serviço local e da camada nativa opcional.",
    "landing.standardMcp": "01 · MCP PADRÃO",
    "landing.mcpClient": "Cliente MCP",
    "landing.streamableHttp": "Streamable HTTP",
    "landing.localRouter": "02 · ROTEADOR LOCAL",
    "landing.localBridge": "Bridge local",
    "landing.execution": "03 · EXECUÇÃO",
    "landing.browserApp": "App BrowserMCP",
    "landing.contextText":
      "Um aplicativo estático pode ser um runtime MCP completo sem servidor próprio ou outra entrada de cliente.",
    "landing.featureBackendTitle": "Sem backend duplicado",
    "landing.featureBackendText":
      "Os handlers executam junto ao estado, armazenamento e APIs Web do app.",
    "landing.featureBridgeTitle": "Um Bridge comum",
    "landing.featureBridgeText":
      "Configure http://127.0.0.1:8789/mcp uma vez e pareie cada app explicitamente.",
    "landing.featureTypedTitle": "Separação tipada",
    "landing.featureTypedText":
      "O MCP padrão e o protocolo interno do Bridge ficam separados e versionados.",
    "landing.featureStaticTitle": "Estático por projeto",
    "landing.featureStaticText":
      "Use npm, ESM, Vite ou hospedagem estática. Nenhum servidor de aplicação é necessário.",
    "landing.featureManyTitle": "Feito para muitos apps",
    "landing.featureManyText":
      "As identidades de Origin, runtime e aba tornam roteamento e colisões inspecionáveis.",
    "landing.featureSecurityTitle": "Segurança estrutural",
    "landing.featureSecurityText":
      "Transporte apenas loopback, Origin exato, limites finitos, cancelamento e logs redigidos.",
    "landing.declareText":
      "A biblioteca web oculta transporte, negociação, correlação, timeout, cancelamento e reconexão atrás de uma API pública pequena.",
    "landing.pairingNote":
      "O browser solicita acesso e o operador aprova o Origin exato no Bridge local. Nenhum token é necessário no browser.",
    "landing.docsText":
      "A documentação visível e o Docs MCP compartilham um corpus tipado com busca entre páginas, consulta de API e tipos, exemplos, diagnóstico, status, fontes e próximas referências.",
    "docs.navigation": "Navegação da documentação",
    "docs.lede":
      "Pesquise conceitos, responsabilidades, API e tipos, exemplos, configuração, protocolo, restrições de segurança e diagnóstico em um corpus estruturado.",
    "docs.pages": "{count} páginas",
    "docs.sections": "{count} seções",
    "docs.implemented": "{count} implementadas",
    "docs.planned": "{count} planejadas",
    "docs.constraints": "{count} restrições",
    "docs.searchHint":
      "Pesquise o índice estruturado. Os resultados incluem status, fonte, página e seção exatas, código e material relacionado.",
    "docs.areasLabel": "Áreas da documentação",
    "docs.sectionCount": "{count} seções →",
    "docs.onThisPageLabel": "Navegação desta página",
    "docs.adjacent": "Páginas de documentação adjacentes",
    "connection.siteRuntime": "Runtime do site BrowserMCP",
    "connection.lede":
      "Todo o site é um app BrowserMCP. Pareie o Origin exato, estabeleça uma sessão e inspecione cada Tool, Resource, Prompt, chamada, resultado e erro seguro.",
    "connection.noSession": "Nenhum ID de sessão ativo",
    "connection.capabilityKinds": "Tools, Resources e Prompts",
    "connection.healthHint": "Execute uma verificação /health sem credenciais",
    "connection.explicitPairing": "Aprovação explícita do Origin",
    "connection.connectText":
      "Toda página HTTPS publicada, inclusive em hospedagem estática, deve usar WSS com certificado local confiável. O desenvolvimento HTTP local pode usar WS em 127.0.0.1. O Bridge aceita apenas URLs loopback.",
    "connection.bridgeUrl": "URL WebSocket do Bridge",
    "connection.tokenNote":
      "O navegador envia o Origin exato e a identidade do app. Aprove a solicitação pendente na administração do Bridge; a página estática não recebe nem salva token de aprovação.",
    "connection.approvalPending":
      "Mantenha esta aba aberta, verifique o Origin exato e a identidade do app no Bridge e escolha Approve ou Reject.",
    "connection.readiness": "Preparação da página segura",
    "connection.fourChecks": "Quatro verificações antes da aprovação",
    "connection.recentError": "Erro de conexão mais recente",
    "connection.diagnosticOrder": "Seguir a ordem de diagnóstico",
    "connection.liveSnapshot": "Visão ao vivo das capacidades",
    "connection.registeredHere": "Registrado neste runtime do site",
    "connection.tools": "Tools",
    "connection.resources": "Resources",
    "connection.prompts": "Prompts",
    "connection.noRegistrations": "Nenhum registro informado.",
    "connection.requests": "Solicitações",
    "connection.noExecutions": "Nenhuma chamada do navegador terminou nesta aba ainda.",
    "connection.outcome": "Resultado",
    "connection.noResult": "Nenhum resultado registrado ainda.",
    "connection.noEvents": "Nenhum evento de diagnóstico ainda.",
    "connection.diagnosticTransport": "Transporte loopback",
    "connection.diagnosticTransportReady": "{endpoint} é um endpoint restrito a loopback.",
    "connection.diagnosticCertificate": "Certificado local confiável",
    "connection.diagnosticCertificateSecure":
      "O navegador deve confiar no certificado localhost do Bridge antes de abrir o WebSocket.",
    "connection.diagnosticCertificateDevelopment":
      "O desenvolvimento HTTP local não usa TLS; páginas HTTPS publicadas devem usar WSS.",
    "connection.diagnosticCertificateAction":
      "Instale e confie explicitamente na CA local gerada e abra https://{host}/health. Não ignore um aviso de certificado.",
    "connection.diagnosticOrigin": "Aprovação do Origin exato",
    "connection.diagnosticOriginDetail":
      "Aprove exatamente {origin}; scheme, host e port são significativos.",
    "connection.diagnosticOriginAction":
      "Solicite acesso aqui e aprove este Origin exato na administração do Bridge.",
    "connection.diagnosticNetwork": "Permissão de rede local",
    "connection.diagnosticNetworkDetail":
      "O Chrome 142 controla solicitações loopback semelhantes a fetch; o Chrome 147 estende a permissão LNA a WebSocket.",
    "connection.diagnosticNetworkAction":
      "Permita o acesso loopback quando solicitado e confirme que a resposta health do Bridge permite este Origin.",
  },
  bn: {
    "common.primaryNavigation": "প্রধান নেভিগেশন",
    "common.idle": "নিষ্ক্রিয়",
    "common.connected": "সংযুক্ত",
    "common.connecting": "সংযোগ হচ্ছে",
    "common.awaitingApproval": "অনুমোদনের অপেক্ষায়",
    "common.reconnecting": "পুনরায় সংযোগ হচ্ছে",
    "common.disconnected": "সংযোগ বিচ্ছিন্ন",
    "common.error": "ত্রুটি",
    "common.registered": "নিবন্ধিত",
    "common.pending": "অপেক্ষমাণ",
    "common.success": "সফল",
    "common.running": "চলমান",
    "common.failed": "ব্যর্থ",
    "common.ready": "প্রস্তুত",
    "common.check": "যাচাই করুন",
    "common.blocked": "অবরুদ্ধ",
    "common.none": "কিছু নেই",
    "common.active": "সক্রিয়",
    "common.resumeAvailable": "পুনরায় শুরু করা যাবে",
    "common.unchecked": "যাচাই করা হয়নি",
    "common.checking": "যাচাই চলছে",
    "common.reachable": "অ্যাক্সেসযোগ্য",
    "common.implemented": "বাস্তবায়িত",
    "common.partial": "আংশিক",
    "common.planned": "পরিকল্পিত",
    "common.constraint": "সীমাবদ্ধতা",
    "common.source": "উৎস: {source}",
    "common.related": "সম্পর্কিত: {reference}",
    "common.codeExample": "{language} কোডের উদাহরণ",
    "notFound.kicker": "404 · রুট পাওয়া যায়নি",
    "notFound.title": "এই রানটাইমে ওই পাথের কোনো পৃষ্ঠা নেই।",
    "notFound.path": "অজানা রুট: {path}",
    "notFound.return": "ডকুমেন্টেশনে ফিরুন",
    "landing.proofEndpoint": "একটি MCP এন্ডপয়েন্ট",
    "landing.proofOrigin": "Origin-ভিত্তিক পেয়ারিং",
    "landing.proofBrowser": "ব্রাউজারে এক্সিকিউশন",
    "landing.architectureText":
      "স্ট্যান্ডার্ড MCP Bridge-এ শেষ হয়। আলাদা টাইপড প্রোটোকল অনুরোধ ব্রাউজারে পাঠায় এবং অ্যাপের লজিক স্থানীয় সেবা ও ঐচ্ছিক নেটিভ শেল থেকে আলাদা রাখে।",
    "landing.standardMcp": "01 · স্ট্যান্ডার্ড MCP",
    "landing.mcpClient": "MCP ক্লায়েন্ট",
    "landing.streamableHttp": "Streamable HTTP",
    "landing.localRouter": "02 · স্থানীয় রাউটার",
    "landing.localBridge": "স্থানীয় Bridge",
    "landing.execution": "03 · এক্সিকিউশন",
    "landing.browserApp": "BrowserMCP অ্যাপ",
    "landing.contextText":
      "নিজস্ব সার্ভার বা অতিরিক্ত ক্লায়েন্ট এন্ট্রি ছাড়াই একটি স্ট্যাটিক অ্যাপ পূর্ণ MCP রানটাইম হতে পারে।",
    "landing.featureBackendTitle": "ডুপ্লিকেট backend নয়",
    "landing.featureBackendText": "হ্যান্ডলার অ্যাপের স্টেট, ব্রাউজার স্টোরেজ ও Web API-এর পাশে চলে।",
    "landing.featureBridgeTitle": "একটি সাধারণ Bridge",
    "landing.featureBridgeText":
      "http://127.0.0.1:8789/mcp একবার কনফিগার করুন, তারপর অ্যাপ স্পষ্টভাবে পেয়ার করুন।",
    "landing.featureTypedTitle": "টাইপড পৃথকীকরণ",
    "landing.featureTypedText": "স্ট্যান্ডার্ড MCP এবং অভ্যন্তরীণ Bridge প্রোটোকল আলাদা ও সংস্করণভুক্ত থাকে।",
    "landing.featureStaticTitle": "স্ট্যাটিক বিতরণের জন্য তৈরি",
    "landing.featureStaticText": "npm, ESM, Vite বা স্ট্যাটিক হোস্ট ব্যবহার করুন। অ্যাপ সার্ভার দরকার নেই।",
    "landing.featureManyTitle": "একাধিক অ্যাপের জন্য",
    "landing.featureManyText": "Origin, রানটাইম ও ট্যাব পরিচয় রাউটিং ও সংঘর্ষ যাচাইযোগ্য করে।",
    "landing.featureSecurityTitle": "কাঠামোগত নিরাপত্তা",
    "landing.featureSecurityText":
      "শুধু loopback ট্রান্সপোর্ট, নির্ভুল Origin অনুমোদন, নির্দিষ্ট সীমা, বাতিলকরণ ও গোপনীয় লগ।",
    "landing.declareText":
      "ওয়েব লাইব্রেরি ছোট পাবলিক API-এর আড়ালে ট্রান্সপোর্ট, নেগোশিয়েশন, মিল, টাইমআউট, বাতিলকরণ ও পুনঃসংযোগ রাখে।",
    "landing.pairingNote":
      "browser access request পাঠায় এবং operator local Bridge-এ exact Origin approve করে। browser-side token লাগে না।",
    "landing.docsText":
      "দৃশ্যমান ডকুমেন্টেশন ও Docs MCP একই টাইপড কর্পাস ব্যবহার করে, যাতে বহু-পৃষ্ঠা অনুসন্ধান, API ও টাইপ খোঁজ, উদাহরণ, সমস্যা সমাধান, অবস্থা, উৎস ও পরবর্তী রেফারেন্স আছে।",
    "docs.navigation": "ডকুমেন্টেশন নেভিগেশন",
    "docs.lede":
      "একটি কাঠামোবদ্ধ কর্পাসে ধারণা, দায়িত্ব, API ও টাইপ, উদাহরণ, কনফিগারেশন, প্রোটোকল, নিরাপত্তা সীমা ও রোগনির্ণয় খুঁজুন।",
    "docs.pages": "{count}টি পৃষ্ঠা",
    "docs.sections": "{count}টি বিভাগ",
    "docs.implemented": "{count}টি বাস্তবায়িত",
    "docs.planned": "{count}টি পরিকল্পিত",
    "docs.constraints": "{count}টি সীমাবদ্ধতা",
    "docs.searchHint":
      "কাঠামোবদ্ধ ইনডেক্স খুঁজুন। ফলাফলে অবস্থা, উৎস, সঠিক পৃষ্ঠা/বিভাগ, কোড ও সম্পর্কিত উপাদান থাকে।",
    "docs.areasLabel": "ডকুমেন্টেশন ক্ষেত্র",
    "docs.sectionCount": "{count}টি বিভাগ →",
    "docs.onThisPageLabel": "এই পৃষ্ঠার নেভিগেশন",
    "docs.adjacent": "পার্শ্ববর্তী ডকুমেন্টেশন পৃষ্ঠা",
    "connection.siteRuntime": "BrowserMCP সাইট রানটাইম",
    "connection.lede":
      "পুরো সাইটটি একটি BrowserMCP অ্যাপ। পৃষ্ঠার সঠিক Origin পেয়ার করুন, ব্রাউজার সেশন তৈরি করুন এবং প্রতিটি Tool, Resource, Prompt, কল, ফলাফল ও নিরাপদ ত্রুটি দেখুন।",
    "connection.noSession": "কোনো সক্রিয় সেশন ID নেই",
    "connection.capabilityKinds": "Tool, Resource ও Prompt",
    "connection.healthHint": "ক্রেডেনশিয়াল ছাড়া /health যাচাই চালান",
    "connection.explicitPairing": "স্পষ্ট Origin অনুমোদন",
    "connection.connectText":
      "স্ট্যাটিক হোস্টিংসহ প্রতিটি প্রকাশিত HTTPS পৃষ্ঠায় বিশ্বস্ত স্থানীয় সার্টিফিকেটসহ WSS ব্যবহার করতে হবে। স্থানীয় HTTP ডেভেলপমেন্ট 127.0.0.1-এ WS ব্যবহার করতে পারে। Bridge শুধু loopback URL গ্রহণ করে।",
    "connection.bridgeUrl": "Bridge WebSocket URL",
    "connection.tokenNote":
      "ব্রাউজার সঠিক Origin ও অ্যাপ পরিচয় পাঠায়। Bridge ব্যবস্থাপনা পৃষ্ঠায় অপেক্ষমাণ অনুরোধ অনুমোদন করুন; স্ট্যাটিক পৃষ্ঠা কোনো অনুমোদন টোকেন পায় বা সংরক্ষণ করে না।",
    "connection.approvalPending":
      "এই ট্যাব খোলা রাখুন, Bridge ব্যবস্থাপনায় সঠিক Origin ও অ্যাপ পরিচয় যাচাই করুন, তারপর Approve বা Reject বেছে নিন।",
    "connection.readiness": "নিরাপদ পৃষ্ঠার প্রস্তুতি",
    "connection.fourChecks": "অনুমোদনের আগে চারটি যাচাই",
    "connection.recentError": "সর্বশেষ সংযোগ ত্রুটি",
    "connection.diagnosticOrder": "রোগনির্ণয়ের ক্রম অনুসরণ করুন",
    "connection.liveSnapshot": "সক্ষমতার লাইভ স্ন্যাপশট",
    "connection.registeredHere": "এই সাইট রানটাইমে নিবন্ধিত",
    "connection.tools": "Tool",
    "connection.resources": "Resource",
    "connection.prompts": "Prompt",
    "connection.noRegistrations": "কোনো নিবন্ধন জানানো হয়নি।",
    "connection.requests": "অনুরোধ",
    "connection.noExecutions": "এই ট্যাবে এখনো কোনো ব্রাউজার কল সম্পন্ন হয়নি।",
    "connection.outcome": "ফলাফল",
    "connection.noResult": "এখনো কোনো ফলাফল রেকর্ড হয়নি।",
    "connection.noEvents": "এখনো কোনো রোগনির্ণয় ইভেন্ট নেই।",
    "connection.diagnosticTransport": "Loopback ট্রান্সপোর্ট",
    "connection.diagnosticTransportReady": "{endpoint} শুধু loopback এন্ডপয়েন্ট।",
    "connection.diagnosticCertificate": "বিশ্বস্ত স্থানীয় সার্টিফিকেট",
    "connection.diagnosticCertificateSecure":
      "WebSocket সেটআপের আগে ব্রাউজারকে Bridge-এর localhost সার্টিফিকেট বিশ্বাস করতে হবে।",
    "connection.diagnosticCertificateDevelopment":
      "স্থানীয় HTTP ডেভেলপমেন্ট TLS ব্যবহার করে না; প্রকাশিত HTTPS পৃষ্ঠায় WSS দরকার।",
    "connection.diagnosticCertificateAction":
      "তৈরি করা স্থানীয় CA ইনস্টল করে স্পষ্টভাবে বিশ্বাস করুন, তারপর https://{host}/health খুলুন। সার্টিফিকেট সতর্কতা এড়িয়ে যাবেন না।",
    "connection.diagnosticOrigin": "নির্ভুল Origin অনুমোদন",
    "connection.diagnosticOriginDetail": "ঠিক {origin} অনুমোদন করুন; scheme, host ও port গুরুত্বপূর্ণ।",
    "connection.diagnosticOriginAction":
      "এখানে অ্যাক্সেস অনুরোধ করুন, তারপর Bridge ব্যবস্থাপনায় এই সঠিক Origin অনুমোদন করুন।",
    "connection.diagnosticNetwork": "স্থানীয় নেটওয়ার্ক অনুমতি",
    "connection.diagnosticNetworkDetail":
      "Chrome 142 fetch-ধরনের loopback অনুরোধ নিয়ন্ত্রণ করে; Chrome 147 LNA অনুমতি WebSocket পর্যন্ত বাড়ায়।",
    "connection.diagnosticNetworkAction":
      "অনুরোধ এলে loopback অ্যাক্সেস অনুমোদন করুন এবং Bridge-এর health উত্তর এই Origin অনুমোদন করে কিনা নিশ্চিত করুন।",
  },
  ru: {
    "common.primaryNavigation": "Основная навигация",
    "common.idle": "Ожидание",
    "common.connected": "Подключено",
    "common.connecting": "Подключение",
    "common.awaitingApproval": "Ожидание одобрения",
    "common.reconnecting": "Переподключение",
    "common.disconnected": "Отключено",
    "common.error": "Ошибка",
    "common.registered": "Зарегистрировано",
    "common.pending": "Ожидание",
    "common.success": "Успешно",
    "common.running": "Выполняется",
    "common.failed": "Сбой",
    "common.ready": "Готово",
    "common.check": "Проверить",
    "common.blocked": "Заблокировано",
    "common.none": "Нет",
    "common.active": "Активно",
    "common.resumeAvailable": "Можно возобновить",
    "common.unchecked": "Не проверено",
    "common.checking": "Проверка",
    "common.reachable": "Доступно",
    "common.implemented": "Реализовано",
    "common.partial": "Частично",
    "common.planned": "Запланировано",
    "common.constraint": "Ограничение",
    "common.source": "Источник: {source}",
    "common.related": "Связано: {reference}",
    "common.codeExample": "Пример кода {language}",
    "notFound.kicker": "404 · Маршрут не найден",
    "notFound.title": "В этой среде нет страницы по такому пути.",
    "notFound.path": "Неизвестный маршрут: {path}",
    "notFound.return": "Вернуться к документации",
    "landing.proofEndpoint": "Одна точка MCP",
    "landing.proofOrigin": "Сопряжение с привязкой к Origin",
    "landing.proofBrowser": "Выполнение в браузере",
    "landing.architectureText":
      "Стандартный MCP заканчивается на Bridge. Отдельный типизированный протокол переносит запросы в браузер и не допускает логику приложения в локальную службу и необязательную нативную оболочку.",
    "landing.standardMcp": "01 · СТАНДАРТНЫЙ MCP",
    "landing.mcpClient": "Клиент MCP",
    "landing.streamableHttp": "Streamable HTTP",
    "landing.localRouter": "02 · ЛОКАЛЬНЫЙ МАРШРУТИЗАТОР",
    "landing.localBridge": "Локальный Bridge",
    "landing.execution": "03 · ВЫПОЛНЕНИЕ",
    "landing.browserApp": "Приложение BrowserMCP",
    "landing.contextText":
      "Статическое приложение может стать полноценной средой MCP без собственного сервера и ещё одной записи клиента.",
    "landing.featureBackendTitle": "Без дублирующего backend",
    "landing.featureBackendText":
      "Обработчики выполняются рядом с состоянием приложения, хранилищем браузера и Web API.",
    "landing.featureBridgeTitle": "Один общий Bridge",
    "landing.featureBridgeText":
      "Один раз настройте http://127.0.0.1:8789/mcp, затем явно сопрягайте приложения.",
    "landing.featureTypedTitle": "Типизированное разделение",
    "landing.featureTypedText":
      "Стандартный MCP и внутренний протокол Bridge разделены и версионируются отдельно.",
    "landing.featureStaticTitle": "Для статического размещения",
    "landing.featureStaticText":
      "Используйте npm, ESM, Vite или статический хост. Сервер приложения не нужен.",
    "landing.featureManyTitle": "Для множества приложений",
    "landing.featureManyText":
      "Origin и идентификаторы среды и вкладки позволяют проверять маршрутизацию и конфликты.",
    "landing.featureSecurityTitle": "Структурная безопасность",
    "landing.featureSecurityText":
      "Только loopback, точный Origin, конечные лимиты, отмена и очищенные журналы.",
    "landing.declareText":
      "Веб-библиотека скрывает транспорт, согласование, сопоставление, тайм-аут, отмену и переподключение за небольшим публичным API.",
    "landing.pairingNote":
      "browser запрашивает доступ, а оператор одобряет exact Origin в локальном Bridge. Token на стороне browser не нужен.",
    "landing.docsText":
      "Видимая документация и Docs MCP используют единый типизированный корпус с поиском между страницами, справкой по API и типам, примерами, диагностикой, статусом, источниками и следующими ссылками.",
    "docs.navigation": "Навигация по документации",
    "docs.lede":
      "Ищите понятия, зоны ответственности, API и типы, примеры, конфигурацию, протокол, ограничения безопасности и диагностику в едином структурированном корпусе.",
    "docs.pages": "Страниц: {count}",
    "docs.sections": "Разделов: {count}",
    "docs.implemented": "Реализовано: {count}",
    "docs.planned": "Запланировано: {count}",
    "docs.constraints": "Ограничений: {count}",
    "docs.searchHint":
      "Поиск по структурированному индексу. Результаты содержат статус, источник, точную страницу и раздел, код и связанные материалы.",
    "docs.areasLabel": "Области документации",
    "docs.sectionCount": "Разделов: {count} →",
    "docs.onThisPageLabel": "Навигация по странице",
    "docs.adjacent": "Соседние страницы документации",
    "connection.siteRuntime": "Среда сайта BrowserMCP",
    "connection.lede":
      "Весь сайт — одно приложение BrowserMCP. Сопрягите точный Origin страницы, создайте сеанс и проверяйте каждый Tool, Resource, Prompt, вызов, результат и безопасную ошибку.",
    "connection.noSession": "Нет активного ID сеанса",
    "connection.capabilityKinds": "Tools, Resources и Prompts",
    "connection.healthHint": "Запустите проверку /health без учётных данных",
    "connection.explicitPairing": "Явное одобрение Origin",
    "connection.connectText":
      "Любая опубликованная HTTPS-страница, включая статический хостинг, должна использовать WSS с доверенным локальным сертификатом. Локальная HTTP-разработка может использовать WS на 127.0.0.1. Bridge принимает только loopback URL.",
    "connection.bridgeUrl": "WebSocket URL для Bridge",
    "connection.tokenNote":
      "Браузер отправляет точный Origin и идентификатор приложения. Одобрите ожидающий запрос в управлении Bridge; статическая страница не получает и не хранит токен одобрения.",
    "connection.approvalPending":
      "Оставьте вкладку открытой, проверьте точный Origin и идентификатор приложения в Bridge, затем выберите Approve или Reject.",
    "connection.readiness": "Готовность защищённой страницы",
    "connection.fourChecks": "Четыре проверки перед одобрением",
    "connection.recentError": "Последняя ошибка подключения",
    "connection.diagnosticOrder": "Следовать порядку диагностики",
    "connection.liveSnapshot": "Текущий снимок возможностей",
    "connection.registeredHere": "Зарегистрировано в среде этого сайта",
    "connection.tools": "Tools",
    "connection.resources": "Resources",
    "connection.prompts": "Prompts",
    "connection.noRegistrations": "Регистрации не обнаружены.",
    "connection.requests": "Запросы",
    "connection.noExecutions": "В этой вкладке ещё не завершился ни один вызов браузера.",
    "connection.outcome": "Результат",
    "connection.noResult": "Результатов пока нет.",
    "connection.noEvents": "Диагностических событий пока нет.",
    "connection.diagnosticTransport": "Транспорт loopback",
    "connection.diagnosticTransportReady": "{endpoint} — точка только для loopback.",
    "connection.diagnosticCertificate": "Доверенный локальный сертификат",
    "connection.diagnosticCertificateSecure":
      "До открытия WebSocket браузер должен доверять сертификату localhost для Bridge.",
    "connection.diagnosticCertificateDevelopment":
      "Локальная HTTP-разработка не использует TLS; опубликованным HTTPS-страницам нужен WSS.",
    "connection.diagnosticCertificateAction":
      "Установите и явно доверьте созданный локальный CA, затем откройте https://{host}/health. Не обходите предупреждение сертификата.",
    "connection.diagnosticOrigin": "Одобрение точного Origin",
    "connection.diagnosticOriginDetail":
      "Одобрите точно {origin}; scheme, host и port имеют значение.",
    "connection.diagnosticOriginAction":
      "Запросите доступ здесь, затем одобрите этот точный Origin в управлении Bridge.",
    "connection.diagnosticNetwork": "Разрешение локальной сети",
    "connection.diagnosticNetworkDetail":
      "Chrome 142 контролирует loopback-запросы типа fetch; Chrome 147 расширяет разрешение LNA на WebSocket.",
    "connection.diagnosticNetworkAction":
      "Разрешите доступ loopback по запросу и убедитесь, что ответ health от Bridge допускает этот Origin.",
  },
};
